import { useId } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  dialogBodySx,
  dialogShellSx,
  dialogTitleSx,
  getDialogCloseButtonSx,
  getDialogHeaderSx,
  getDialogPaperSx,
} from "components/common/dialogSurfaceStyles";
import { pageHelpButtonSx } from "components/common/pageHelpButtonStyles";

export interface PageInfoContent {
  title: string;
  description: string;
  dataShown?: string[];
  actions: string[];
}

interface PageHelpButtonProps {
  open: boolean;
  pageInfo: PageInfoContent;
  onClose: () => void;
  onOpen: () => void;
  sx?: SxProps<Theme>;
}

const renderList = (items: string[]) => (
  <List dense disablePadding sx={{ pl: 0.5 }}>
    {items.map((item, index) => (
      <ListItem
        key={`${index}-${item}`}
        sx={{ display: "list-item", listStyleType: "disc", ml: 2, py: 0.25, pl: 0.25 }}
      >
        <Typography variant="body2" color="text.secondary">
          {item}
        </Typography>
      </ListItem>
    ))}
  </List>
);

export default function PageHelpButton({
  open,
  pageInfo,
  onClose,
  onOpen,
  sx = pageHelpButtonSx,
}: PageHelpButtonProps) {
  const baseId = useId();
  const dialogTitleId = `${baseId}-title`;
  const dialogDescriptionId = `${baseId}-description`;
  const dataShown = pageInfo.dataShown ?? [];
  const hasDataShown = dataShown.length > 0;

  return (
    <>
      <Tooltip title={`View information about ${pageInfo.title}`}>
        <Button
          type="button"
          size="small"
          startIcon={<InfoOutlinedIcon fontSize="small" />}
          onClick={onOpen}
          aria-label={`Open information about ${pageInfo.title}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={sx}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Info
          </Box>
        </Button>
      </Tooltip>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        PaperProps={{
          sx: (theme) => ({
            ...getDialogPaperSx(theme),
            borderRadius: 0,
          }),
        }}
      >
        <Box sx={dialogShellSx}>
          <Box sx={(theme) => getDialogHeaderSx(theme)}>
            <Typography id={dialogTitleId} variant="h6" sx={dialogTitleSx}>
              {pageInfo.title}
            </Typography>
            <IconButton
              aria-label="Close information dialog"
              onClick={onClose}
              size="small"
              sx={(theme) => getDialogCloseButtonSx(theme)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <DialogContent sx={dialogBodySx}>
            <Stack spacing={2}>
              <Typography id={dialogDescriptionId} variant="body2" color="text.secondary">
                {pageInfo.description}
              </Typography>

              {hasDataShown && (
                <Box>
                  <Typography variant="subtitle2" color="text.primary" fontWeight={700} gutterBottom>
                    Data shown
                  </Typography>
                  {renderList(dataShown)}
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.primary" fontWeight={700} gutterBottom>
                  What you can do
                </Typography>
                {renderList(pageInfo.actions)}
              </Box>
            </Stack>
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
}
